// eslint-disable-next-line
import React, { ReactNode, useEffect, useState } from 'react';

import {
  CustomRadioGroup,
  CustomTextField,
  FormSubTitle,
  KeyboardDatePicker,
  WizardControlsWithFormik,
  WizardForm,
  InputWrapper,
} from 'components/forms';
import { Field, useFormikContext } from 'formik';
import { PhoneFields, PhoneTypes } from 'core/phones';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import { Nullable } from 'utils/types';
import { RGX_NO_SPECIAL_CHARACTERS, RGX_ONLY_NUMBERS } from 'utils/helpers';
import Radio from '@material-ui/core/Radio';
import { selectSearchError, selectMainSearchClient } from 'store/modules/main';
import { useSelector } from 'react-redux';
import SlideAnimation from 'components/SlideAnimation';
import SelectDocument from 'core/document-types/SelectDocument';
import { useGetDocTypes } from 'core/document-types';
import styled from 'styled-components';
import { spacing } from '@material-ui/system';
import { useIsEmployeeFlow } from 'core/employee';
import { isRuc, isCompanyRuc } from 'core/document-types/helpers';
import dictionary from 'utils/dictionary';
import { usePrevious } from 'utils/hooks';
import { Client } from '../client.model';
import ContactListSubForm, {
  ContactListInitialValues,
} from './ContactListSubForm';

const GridBox = styled(Grid)`
  ${spacing}
`;

export type FormValues = {
  name: string;
  surname: string;
  sex: string;
  birthDate: Nullable<Date>;
  phoneType: string;
  areaCode: string;
  phoneNumber: string;
  phoneCompany: string;
  email: string;
  contacts: ContactListInitialValues;
  documentType?: string;
  documentNumber?: string;
  company?: string;
};

type Props = {
  alertRisk: ReactNode;
  checkRisk: (values: { name: string; surname: string; sex: string }) => void;
  client?: Client | null;
  hasDebt?: boolean;
  hasFraud?: boolean;
  hasLoadRisk: boolean;
};

export const ADDITIONAL_CONTACTS_SUBFORM = 'contacts';
interface CompanyDisabled {
  documentType: boolean;
  documentNumber: boolean;
  name: boolean;
  surname: boolean;
  sexM: boolean;
  sexF: boolean;
  birthDate: boolean;
  submit: boolean;
  company: boolean;
}

const ClientForm: React.FC<Props> = ({
  alertRisk,
  checkRisk,
  client,
  hasDebt = false,
  hasFraud = false,
  hasLoadRisk,
}) => {
  const { initialErrors, values, errors, setFieldValue } = useFormikContext<
    FormValues
  >();

  const mainError = useSelector(selectSearchError);
  const { params } = useSelector(selectMainSearchClient);

  const { name, surname, sex, company } = values;
  const [notCompanyDisabledForm, setNotCompanyDisabledForm] = useState<
    CompanyDisabled
  >({
    documentType: true,
    documentNumber: true,
    name: true,
    surname: true,
    sexM: true,
    sexF: true,
    birthDate: true,
    submit: true,
    company: true,
  });
  const [companyDisabledForm, setCompanyDisabledForm] = useState<
    CompanyDisabled
  >({
    documentType: false,
    documentNumber: false,
    name: false,
    surname: false,
    sexM: false,
    sexF: false,
    birthDate: false,
    submit: true,
    company: true,
  });
  const [disabledForm, setDisabledForm] = useState<CompanyDisabled>({
    documentType: false,
    documentNumber: false,
    name: false,
    surname: false,
    sexM: false,
    sexF: false,
    birthDate: false,
    submit: true,
    company: true,
  });

  const isEmployeeFlow = useIsEmployeeFlow();
  const previousNameValue = usePrevious(values.name);
  const previousSurnameValue = usePrevious(values.surname);
  const previousGenderValue = usePrevious(values.sex);
  const docTypesAsyncState = useGetDocTypes(['RUC']);

  useEffect(() => {
    setNotCompanyDisabledForm({
      documentType: !!client?.documentType && !initialErrors.documentType,
      documentNumber: !!client?.documentNumber && !initialErrors.documentNumber,
      name:
        (!!client?.name && !initialErrors.name) ||
        (client?.isClient === 'N' && !client?.name && isEmployeeFlow),
      surname:
        (!!client?.surname && !initialErrors.surname) ||
        (client?.isClient === 'N' && !client?.surname && isEmployeeFlow),
      sexM:
        (!isEmployeeFlow && !!client?.sex && !initialErrors.sex) ||
        (isEmployeeFlow &&
          client?.isClient === 'S' &&
          !!client?.sex &&
          !initialErrors.sex),
      sexF:
        (!isEmployeeFlow && !!client?.sex && !initialErrors.sex) ||
        (isEmployeeFlow &&
          client?.isClient === 'S' &&
          !!client?.sex &&
          !initialErrors.sex),
      birthDate:
        (!!client?.birthDate && !initialErrors.birthDate) ||
        (isEmployeeFlow &&
          client?.isClient === 'N' &&
          !!initialErrors.birthDate),
      submit:
        !hasLoadRisk ||
        Object.keys(errors).length > 0 ||
        Boolean(mainError) ||
        hasDebt ||
        hasFraud ||
        (!!client?.exonerationCard && !client?.exonerationCard?.active),
      company:
        (!!client?.company && !initialErrors.company) ||
        (!client?.company && isEmployeeFlow),
    });
  }, [
    client,
    initialErrors,
    isEmployeeFlow,
    errors,
    mainError,
    hasLoadRisk,
    hasDebt,
    hasFraud,
  ]);

  useEffect(() => {
    setCompanyDisabledForm({
      documentType: false,
      documentNumber: false,
      name: false,
      surname: false,
      sexM: false,
      sexF: false,
      birthDate: false,
      submit:
        !hasLoadRisk ||
        Object.keys(errors).length > 0 ||
        Boolean(mainError) ||
        hasDebt ||
        (!!client?.exonerationCard && !client?.exonerationCard?.active),
      company: !!client?.company,
    });
  }, [client, hasDebt, hasLoadRisk, errors, mainError]);

  useEffect(() => {
    setDisabledForm(
      isRuc(params.documentType) &&
        isCompanyRuc(params.documentNumber + params.validatorDigit)
        ? companyDisabledForm
        : notCompanyDisabledForm
    );
  }, [params, companyDisabledForm, notCompanyDisabledForm]);

  useEffect(() => {
    if (values.phoneType === PhoneTypes.Fijo) {
      setFieldValue('phoneCompany', '');
    }
  }, [values.phoneType, setFieldValue]);

  const handleCheckRisk = () => {
    if (
      isRuc(params.documentType) &&
      isCompanyRuc(params.documentNumber + params.validatorDigit) &&
      company
    ) {
      if (!errors.company && company) {
        checkRisk({
          name: company.slice(30, 60),
          surname: company.slice(0, 30),
          sex: 'C',
        });
      }
    } else {
      const noErrors = !errors.name && !errors.surname;
      const haveValues = Boolean(name && surname && sex);

      const valuesChanged =
        previousNameValue !== name ||
        previousSurnameValue !== surname ||
        previousGenderValue !== sex;

      if (haveValues && noErrors && valuesChanged) {
        checkRisk({ name, surname, sex });
      }
    }
  };

  useEffect(() => {
    if (!sex && !client) return;
    handleCheckRisk();
    // eslint-disable-next-line
  }, [sex, client]);

  const handleAutocompleteChange = (field: string, value: string) => {
    setFieldValue(field, value);
  };

  return (
    <WizardForm>
      <SlideAnimation direction="up" delay={0.5}>
        {!isRuc(params.documentType) && (
          <FormSubTitle>{dictionary.clientForm_subtitle}</FormSubTitle>
        )}
        {isRuc(params.documentType) &&
          isCompanyRuc(params.documentNumber + params.validatorDigit) && (
            <>
              <GridBox mb={4} container>
                <InputWrapper width="550px">
                  <Field
                    id="clientFormCompany"
                    name="company"
                    label={dictionary.clientForm_label_company}
                    component={CustomTextField}
                    inputProps={{ onBlur: handleCheckRisk }}
                    disabled={disabledForm.company}
                  />
                </InputWrapper>
              </GridBox>
              <GridBox mb={4} container>
                {alertRisk}
              </GridBox>
            </>
          )}
        {isRuc(params.documentType) &&
          isCompanyRuc(params.documentNumber + params.validatorDigit) && (
            <FormSubTitle>{dictionary.clientForm_ruc_subtitle}</FormSubTitle>
          )}
        <GridBox mb={4} container>
          {isRuc(params.documentType) &&
            isCompanyRuc(params.documentNumber + params.validatorDigit) && (
              <>
                <InputWrapper width="115px">
                  <Field
                    id="clientFormDocumentType"
                    name="documentType"
                    label={dictionary.clientForm_label_documentType}
                    component={SelectDocument}
                    asyncState={docTypesAsyncState}
                    disabled={disabledForm.documentType}
                  />
                </InputWrapper>
                <InputWrapper width="110px">
                  <Field
                    id="clientFormDocumentNumber"
                    name="documentNumber"
                    label={dictionary.clientForm_label_documentNumber}
                    regex={RGX_ONLY_NUMBERS}
                    component={CustomTextField}
                    inputProps={{ onBlur: handleCheckRisk }} // ??
                    disabled={disabledForm.documentNumber}
                  />
                </InputWrapper>
              </>
            )}
          <InputWrapper>
            <Field
              id="clientFormName"
              name="name"
              label={dictionary.clientForm_label_name}
              regex={RGX_NO_SPECIAL_CHARACTERS}
              component={CustomTextField}
              inputProps={{ onBlur: handleCheckRisk }}
              disabled={disabledForm.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleAutocompleteChange('name', e.target.value)
              }
            />
          </InputWrapper>
          <InputWrapper>
            <Field
              id="clientFormSurname"
              label={dictionary.clientForm_label_surname}
              name="surname"
              regex={RGX_NO_SPECIAL_CHARACTERS}
              component={CustomTextField}
              inputProps={{ onBlur: handleCheckRisk }}
              disabled={disabledForm.surname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleAutocompleteChange('surname', e.target.value)
              }
            />
          </InputWrapper>

          <Field
            name="sex"
            label={dictionary.clientForm_label_sex}
            component={CustomRadioGroup}
          >
            <FormControlLabel
              value="M"
              control={<Radio color="primary" disabled={disabledForm.sexM} />}
              label={
                isRuc(params.documentType) &&
                isCompanyRuc(params.documentNumber + params.validatorDigit)
                  ? dictionary.clientForm_label_sex_m_ruc
                  : dictionary.clientForm_label_sex_m
              }
            />
            <FormControlLabel
              value="F"
              control={<Radio color="primary" disabled={disabledForm.sexF} />}
              label={
                isRuc(params.documentType) &&
                isCompanyRuc(params.documentNumber + params.validatorDigit)
                  ? dictionary.clientForm_label_sex_f_ruc
                  : dictionary.clientForm_label_sex_f
              }
            />
          </Field>
          <InputWrapper>
            <Field
              id="clientFormDate"
              label={
                isRuc(params.documentType) &&
                isCompanyRuc(params.documentNumber + params.validatorDigit)
                  ? dictionary.clientForm_label_birthDate_ruc
                  : dictionary.clientForm_label_birthDate
              }
              name="birthDate"
              disabled={disabledForm.birthDate}
              disableFuture
              component={KeyboardDatePicker}
            />
          </InputWrapper>
        </GridBox>
        {!isCompanyRuc(params.documentNumber + params.validatorDigit) && (
          <GridBox mb={4} container>
            {alertRisk}
          </GridBox>
        )}
        <FormSubTitle>{dictionary.clientForm_contact_subtitle}</FormSubTitle>
        <GridBox mb={4} container>
          <PhoneFields />
          <InputWrapper>
            <Field
              id="clientFormEmail"
              label={dictionary.clientForm_label_email}
              name="email"
              component={CustomTextField}
            />
          </InputWrapper>
        </GridBox>
        <ContactListSubForm namespace={ADDITIONAL_CONTACTS_SUBFORM} />
      </SlideAnimation>
      <WizardControlsWithFormik disableSubmit={disabledForm.submit} />
    </WizardForm>
  );
};

export default ClientForm;
